import { useState, useEffect } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

export default function InvoicesDetailPage({ dist, getToken, showToast, navigate, invoiceId }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copying, setCopying] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalLoading, setEmailModalLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  useEffect(() => {
    loadInvoice();
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const r = await api.withToken(token).getInvoice(invoiceId);
      if (r.ok && r.invoice) {
        setInvoice(r.invoice);
      } else {
        setError('Invoice not found');
      }
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!invoice?.share_token) return;
    try {
      setCopying(true);
      const link = `${window.location.origin}/invoices/${invoice.share_token}`;
      await navigator.clipboard.writeText(link);
      showToast('Link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
      showToast('Failed to copy link', 'error');
    } finally {
      setCopying(false);
    }
  };

  const openEmailModal = async () => {
    setEmailModalLoading(true);
    setEmailModalOpen(true);
    try {
      const token = await getToken();
      const draft = await api.withToken(token).getInvoiceEmailDraft(invoiceId);
      setEmailTo(draft.to || '');
      setEmailCc('');
      setEmailSubject(draft.subject || '');
      setEmailBody(draft.body || '');
    } catch (err) {
      console.error('Failed to load email draft:', err);
      showToast('Failed to load email draft', 'error');
      setEmailModalOpen(false);
    } finally {
      setEmailModalLoading(false);
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const token = await getToken();
      const result = await api.withToken(token).emailInvoice(invoiceId, {
        to: emailTo, cc: emailCc, subject: emailSubject, body: emailBody,
      });
      if (result.ok) {
        showToast('Invoice sent to client');
        setEmailModalOpen(false);
      } else {
        showToast(result.error || 'Failed to send', 'error');
      }
    } catch (err) {
      console.error('Failed to send invoice email:', err);
      showToast(err.message || 'Failed to send', 'error');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    try {
      return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const formatCurrency = (n) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
    } catch {
      return '$' + (n || 0).toFixed(2);
    }
  };

  const getLineItems = () => {
    if (!invoice) return [];
    try {
      return Array.isArray(invoice.line_items) 
        ? invoice.line_items 
        : JSON.parse(invoice.line_items || '[]');
    } catch (err) {
      console.error('Failed to parse line items:', err);
      return [];
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      unpaid: { bg: '#FEF3C7', color: '#92400E' },
      paid: { bg: '#D1FAE5', color: '#065F46' },
      failed: { bg: '#FEE2E2', color: '#7F1D1D' },
    };
    const s = styles[status] || styles.unpaid;
    return (
      <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 6, background: s.bg, color: s.color, fontSize: 12, fontWeight: 500 }}>
        {status === 'unpaid' ? 'Unpaid' : status === 'paid' ? 'Paid ✓' : 'Failed'}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading...</div>;
  }

  if (error || !invoice) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ color: '#EF4444', marginBottom: '16px', fontWeight: 600 }}>{error || 'Invoice not found'}</div>
        <button
          onClick={() => navigate('invoices')}
          style={{
            padding: '12px 24px',
            background: '#000',
            color: '#FFF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Back to Invoices
        </button>
      </div>
    );
  }

  const lineItems = getLineItems();

  return (
    <div style={{ padding: '24px', maxWidth: '700px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: 28, fontWeight: 600 }}>Invoice {invoice.invoice_number}</h1>
          <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
            Created {formatDate(invoice.created_at)}
            {invoice.paid_at && ` • Paid ${formatDate(invoice.paid_at)}`}
          </p>
        </div>
        {getStatusBadge(invoice.payment_status)}
      </div>

      {/* Actions */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '8px' }}>
        <button
          onClick={copyLink}
          disabled={copying}
          style={{
            padding: '12px 16px',
            border: '1px solid #D1D5DB',
            background: '#FFF',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {copying ? 'Copying...' : 'Copy Link'}
        </button>
        <button
          onClick={openEmailModal}
          style={{
            padding: '12px 16px',
            background: G,
            color: '#FFF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Send Invoice
        </button>
        {invoice.payment_status === 'paid' && (
          <button
            onClick={() => navigate('presentations')} // Could open PO creation for this invoice
            style={{
              padding: '12px 16px',
              background: '#000',
              color: '#FFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Create POs
          </button>
        )}
      </div>

      {/* Client info */}
      {(invoice.client_name || invoice.client_email) && (
        <div style={{ marginBottom: '24px', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', marginBottom: '8px' }}>Client</div>
          {invoice.client_name && <div style={{ fontSize: 14, fontWeight: 500 }}>{invoice.client_name}</div>}
          {invoice.client_email && <div style={{ fontSize: 14, color: '#666' }}>{invoice.client_email}</div>}
        </div>
      )}

      {/* Ship to (set at creation; see CreateInvoiceModal) */}
      {invoice.ship_to && (invoice.ship_to.address1 || invoice.ship_to.company || invoice.ship_to.name) && (
        <div style={{ marginBottom: '24px', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', marginBottom: '8px' }}>Ship to</div>
          <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
            {invoice.ship_to.name && <div style={{ fontWeight: 500 }}>{invoice.ship_to.name}</div>}
            {invoice.ship_to.company && <div>{invoice.ship_to.company}</div>}
            {invoice.ship_to.address1 && <div>{invoice.ship_to.address1}</div>}
            {invoice.ship_to.address2 && <div>{invoice.ship_to.address2}</div>}
            {(invoice.ship_to.city || invoice.ship_to.state || invoice.ship_to.zip) && (
              <div>{[invoice.ship_to.city, invoice.ship_to.state].filter(Boolean).join(', ')} {invoice.ship_to.zip || ''}</div>
            )}
            {invoice.ship_to.phone && <div>{invoice.ship_to.phone}</div>}
          </div>
        </div>
      )}

      {/* Line items */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: '12px' }}>Items</h3>
        <div style={{ background: '#F9FAFB', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E7EB', background: '#F3F4F6' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: 12, fontWeight: 600, color: '#666' }}>Description</th>
                <th style={{ textAlign: 'right', padding: '12px', fontSize: 12, fontWeight: 600, color: '#666' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ padding: '12px', textAlign: 'center', color: '#999' }}>No items</td>
                </tr>
              ) : (
                lineItems.map((item, i) => (
                  <tr key={i} style={{ borderBottom: i < lineItems.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                    <td style={{ padding: '12px', fontSize: 14 }}>
                      <div>{item.description || 'Item'}</div>
                      {item.qty && <div style={{ fontSize: 12, color: '#666' }}>Qty: {item.qty}</div>}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px', fontSize: 14, fontWeight: 500 }}>
                      {formatCurrency(item.subtotal || 0)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div style={{ marginBottom: '24px', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: 14 }}>
          <div>Subtotal</div>
          <div>{formatCurrency(invoice.subtotal)}</div>
        </div>
        {invoice.tax_amount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: 14 }}>
            <div>{invoice.tax_label || 'Tax'}</div>
            <div>{formatCurrency(invoice.tax_amount)}</div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 600, paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
          <div>Total</div>
          <div>{formatCurrency(invoice.total)}</div>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: '8px' }}>Notes</h3>
          <div style={{ padding: '12px', background: '#F9FAFB', borderRadius: '8px', whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: '1.6', color: '#333' }}>
            {invoice.notes}
          </div>
        </div>
      )}

      {/* Share link */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: '8px' }}>Share with Client</h3>
        <div style={{ padding: '12px', background: '#F9FAFB', borderRadius: '8px', fontSize: 13, color: '#666', wordBreak: 'break-all' }}>
          {`${window.location.origin}/invoices/${invoice.share_token}`}
        </div>
      </div>

      <button
        onClick={() => navigate('invoices')}
        style={{
          padding: '12px 24px',
          border: '1px solid #D1D5DB',
          background: '#FFF',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        ← Back to Invoices
      </button>

      {emailModalOpen && (
        <EmailComposeModal
          loading={emailModalLoading}
          sending={sending}
          to={emailTo} setTo={setEmailTo}
          cc={emailCc} setCc={setEmailCc}
          subject={emailSubject} setSubject={setEmailSubject}
          body={emailBody} setBody={setEmailBody}
          onSend={sendEmail}
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </div>
  );
}

// ─── EmailComposeModal: review/edit the invoice email before it sends ───────
// A local copy of the same proven pattern used for PO emails (PODetail.jsx),
// duplicated here rather than shared/imported so invoice code stays fully
// self-contained — every field is editable before the real send fires.
function EmailComposeModal({ loading, sending, to, setTo, cc, setCc, subject, setSubject, body, setBody, onSend, onClose }) {
  const fieldLabel = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' };
  const fieldInput = {
    width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6,
    fontSize: 13, fontFamily: 'inherit', outline: 'none', color: '#0f172a', boxSizing: 'border-box',
  };
  const btnPrimary = {
    padding: '8px 16px', background: G, color: '#fff', border: 'none',
    borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  };
  const btnSecondary = {
    padding: '8px 16px', background: '#fff', color: '#334155',
    border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13,
    fontWeight: 600, cursor: 'pointer',
  };
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 10, width: '100%', maxWidth: 560, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Email invoice to client</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading draft…</div>
        ) : (
          <>
            <div style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={fieldLabel}>To</label>
                <input value={to} onChange={e => setTo(e.target.value)} placeholder="client@example.com" style={fieldInput} />
              </div>
              <div>
                <label style={fieldLabel}>Cc <span style={{ fontWeight: 400, textTransform: 'none', color: '#cbd5e1' }}>(optional)</span></label>
                <input value={cc} onChange={e => setCc(e.target.value)} placeholder="you@yourcompany.com" style={fieldInput} />
              </div>
              <div>
                <label style={fieldLabel}>Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} style={fieldInput} />
              </div>
              <div>
                <label style={fieldLabel}>Message</label>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={8}
                  style={{ ...fieldInput, resize: 'vertical', lineHeight: 1.5 }} />
              </div>
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={onClose} disabled={sending} style={btnSecondary}>Cancel</button>
              <button onClick={onSend} disabled={sending || !to.trim()} style={{ ...btnPrimary, opacity: (sending || !to.trim()) ? 0.6 : 1 }}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
